/**
 * Merge ATS tailored reanalysis into displayed match data only when scores
 * strictly improve (see plan: global-first else legacy overall_score).
 */

import { computeRelativeBoostPercent } from '@/lib/match-scoring'
import type { JobApplyResumeStatus } from '@/types'

const APPLY_DONE = new Set(['ready', 'ready_reanalysis_failed'])

export interface TailoredMatchOverlay {
  overall_score: number
  global_score?: number | null
  matched_skills?: string[]
  missing_skills?: string[]
  match_summary?: string | undefined
}

/** Narrow helper — callers pass analyzed-job match + optional apply-resume run. */
export function isStrictTailoredImprovement(run: JobApplyResumeStatus | null | undefined): boolean {
  if (!run) return false

  const prevG = run.previous_global_score
  const newG = run.new_global_score
  if (
    typeof prevG === 'number' &&
    typeof newG === 'number' &&
    !Number.isNaN(prevG) &&
    !Number.isNaN(newG)
  ) {
    return newG > prevG
  }

  const prevS = run.previous_score
  const newS = run.new_score
  if (typeof prevS === 'number' && typeof newS === 'number') {
    if (newS > prevS) return true
    if (newS < prevS) return false // regression — don't show boost
    // equal: LLM gave no signal, fall through to coverage delta
  }

  // Keyword coverage delta: the LLM match score may not change (same qualifications)
  // but the resume genuinely improved if more JD terms appear in the tailored content.
  if (typeof run.delta_percent === 'number') {
    return run.delta_percent > 0
  }

  return false
}

export function computeTailoredBoostPercent(
  run: JobApplyResumeStatus | null | undefined,
): number | null {
  if (!run) return null
  // Prefer the explicit keyword coverage delta — this is the honest signal.
  // The LLM score difference is unreliable: the same qualifications score differently
  // across calls because ATS-optimized language raises cv_match without real improvement.
  if (typeof run.delta_percent === 'number') return run.delta_percent
  return computeRelativeBoostPercent({
    previousGlobalScore: run.previous_global_score ?? null,
    newGlobalScore: run.new_global_score ?? null,
    previousScore: run.previous_score ?? null,
    newScore: run.new_score ?? null,
  })
}

export function mergeTailoredReanalysisIntoMatch<T extends TailoredMatchOverlay | null>(
  base: T,
  _run: JobApplyResumeStatus | null | undefined,
): T {
  // All match data (score, skills, summary) stays frozen from the original analysis.
  // Qualifications didn't change — only the resume's presentation did.
  // The boost % label is shown separately via tailoredDisplayBoostPercent.
  return base
}

export function tailoredDisplayBoostPercent(
  run: JobApplyResumeStatus | null | undefined,
): number | null {
  if (!run || !isStrictTailoredImprovement(run)) {
    return null
  }
  const boost = computeTailoredBoostPercent(run)
  return typeof boost === 'number' && boost > 0 ? boost : null
}

export function isApplyResumeDone(run: JobApplyResumeStatus | null | undefined): boolean {
  return Boolean(run?.status && APPLY_DONE.has(run.status))
}

function positiveIntOrNull(value: unknown): number | null {
  const n =
    typeof value === 'number' ? value : typeof value === 'string' ? Number(value) : Number.NaN
  if (!Number.isFinite(n) || n <= 0) return null
  return Math.round(n)
}

/** Positive keyword-coverage boost for coach labels, or null when none to show. */
export function resolveResumeBoostPercent(
  run: JobApplyResumeStatus | null | undefined,
): number | null {
  if (!isApplyResumeDone(run)) return null
  // Prefer server-computed keyword delta (authoritative for apply-resume).
  const fromDelta = positiveIntOrNull(run?.delta_percent)
  if (fromDelta != null) return fromDelta
  return tailoredDisplayBoostPercent(run)
}
